<template>
  <div v-if="banner && banner.image_url" :class="bannerWrapperClass" :style="backgroundStyle">
    <div :class="containerClass" :style="containerStyle">
      <div v-if="hasTextOverlay" class="flex flex-col xl:flex-row xl:items-stretch w-full min-w-0" :style="bannerStyle">
        <div v-if="banner.text_position === 'right'"
          class="w-full xl:flex-1 p-4 xl:p-8 flex flex-col justify-center min-w-0 overflow-hidden"
          :class="textOrderClass" :style="contentStyle">
          <div v-if="banner.text_subheading" :class="[
            banner.text_font || 'font-marketing',
            banner.text_size_class || 'text-xl xl:text-2xl',
            'mb-3 text-left break-words overflow-hidden'
          ]"
            :style="{ color: banner.text_color || '#111111', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }"
            v-html="banner.text_subheading">
          </div>
          <div v-if="banner.text_overlay" :class="[
            banner.text_font || 'font-marketing',
            banner.text_size_class ? `${banner.text_size_class.replace('text-', 'text-sm ')} leading-relaxed` : 'text-base xl:text-lg leading-relaxed',
            'text-left break-words overflow-hidden'
          ]"
            :style="{ color: banner.text_color || '#111111', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }"
            v-html="banner.text_overlay">
          </div>
        </div>
        <div class="relative overflow-hidden w-full xl:flex-shrink-0 border-gray-200 flex items-center justify-center p-4 xl:p-8"
          :class="[imageBorderClass, imageOrderClass]" :style="imageStyle">
          <a v-if="banner.link_url" :href="banner.link_url" class="flex items-center justify-center w-full h-full">
            <img :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
              class="w-full h-full" />
          </a>
          <img v-else :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
            class="w-full h-full" />
        </div>
        <div v-if="banner.text_position === 'left'"
          class="w-full xl:flex-1 p-4 xl:p-8 flex flex-col justify-center min-w-0 overflow-hidden"
          :class="textOrderClass" :style="contentStyle">
          <div v-if="banner.text_subheading" :class="[
            banner.text_font || 'font-marketing',
            banner.text_size_class || 'text-xl xl:text-2xl',
            'mb-3 text-left break-words overflow-hidden'
          ]"
            :style="{ color: banner.text_color || '#111111', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }"
            v-html="banner.text_subheading">
          </div>
          <div v-if="banner.text_overlay" :class="[
            banner.text_font || 'font-marketing',
            banner.text_size_class ? `${banner.text_size_class.replace('text-', 'text-sm ')} leading-relaxed` : 'text-base xl:text-lg leading-relaxed',
            'text-left break-words overflow-hidden'
          ]"
            :style="{ color: banner.text_color || '#111111', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }"
            v-html="banner.text_overlay">
          </div>
        </div>
      </div>
      <div v-else class="flex justify-center items-center w-full" :style="bannerStyle">
        <div class="relative overflow-hidden w-full max-w-full flex items-center justify-center" :style="imageStyle">
          <a v-if="banner.link_url" :href="banner.link_url"
            class="flex items-center justify-center w-full h-full min-w-0">
            <img :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
              class="w-full h-full" />
          </a>
          <img v-else :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
            class="w-full h-full" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  banner: {
    type: Object,
    required: true
  }
})

const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 800)
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1280)

const updateViewportSize = () => {
  if (typeof window !== 'undefined') {
    viewportHeight.value = window.innerHeight
    viewportWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    viewportHeight.value = window.innerHeight
    viewportWidth.value = window.innerWidth
    window.addEventListener('resize', updateViewportSize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateViewportSize)
  }
})

const bannerWrapperClass = computed(() => {
  if (!hasTextOverlay.value) {
    return 'w-full overflow-hidden'
  }
  const baseClasses = 'w-full shadow-lg py-6 md:py-8 border-t border-b border-gray-200 overflow-hidden'
  if (props.banner.position === 'top') {
    return `${baseClasses} mt-6 md:mt-8 mb-6 md:mb-8`
  } else if (props.banner.position === 'bottom') {
    return `${baseClasses} mt-6 md:mt-8 mb-6 md:mb-8`
  }
  return baseClasses
})

const containerClass = computed(() => {
  if (!hasTextOverlay.value) {
    if (props.banner.position === 'top' || props.banner.position === 'bottom') {
      return 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full overflow-hidden'
    }
    return 'w-full overflow-hidden'
  }
  const baseClasses = 'pt-4 md:pt-6 pb-4 md:pb-6 overflow-hidden'
  if (props.banner.position === 'top' || props.banner.position === 'bottom') {
    return `max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${baseClasses}`
  }
  return `w-full ${baseClasses}`
})

const containerStyle = computed(() => {
  const style = {
    overflow: 'hidden'
  }
  if (!hasTextOverlay.value) {
    return style
  }
  // Only apply max-height on desktop (>= 1280px) to prevent overflow
  // On mobile/tablet (< 1280px), let content determine height naturally
  if (viewportWidth.value >= 1280) {
    if (props.banner.max_height) {
      const maxHeight = Math.min(props.banner.max_height, viewportHeight.value * 0.8)
      style.maxHeight = `${maxHeight}px`
    } else {
      style.maxHeight = `${viewportHeight.value * 0.8}px`
    }
  } else {
    // On mobile, ensure natural height with no constraints
    style.height = 'auto'
    style.minHeight = 'auto'
  }
  return style
})

const bannerStyle = computed(() => {
  // Only apply max-height on desktop (>= 1280px)
  // On mobile/tablet (< 1280px), let content flow naturally
  if (viewportWidth.value >= 1280) {
    if (props.banner.max_height) {
      const maxHeight = Math.min(props.banner.max_height, viewportHeight.value * 0.8)
      return {
        maxHeight: `${maxHeight}px`
      }
    }
    return {
      maxHeight: `${viewportHeight.value * 0.8}px`
    }
  }
  // Mobile: no height constraints, just let content stack naturally
  return {
    height: 'auto',
    minHeight: 'auto'
  }
})

const hasTextOverlay = computed(() => {
  return !!(props.banner.text_overlay || props.banner.text_subheading)
})

const imageBorderClass = computed(() => {
  if (!hasTextOverlay.value) return ''
  return props.banner.text_position === 'right' ? 'xl:border-l' : 'xl:border-r'
})

const imageStyle = computed(() => {
  const hasText = hasTextOverlay.value

  if (!hasText) {
    return {
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }

  const baseStyle = {
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box'
  }

  // On mobile/tablet (< 1280px), allow full width and auto height
  if (viewportWidth.value < 1280) {
    baseStyle.width = '100%'
    baseStyle.maxWidth = '100%'
    baseStyle.minHeight = '200px'
    baseStyle.height = 'auto'
  } else {
    // Desktop: 50% width accounting for border
    baseStyle.flex = '0 0 50%'
    baseStyle.maxWidth = '50%'
    baseStyle.width = '50%'
    baseStyle.minHeight = '300px'
    // Let height be determined by the parent container
    baseStyle.height = '100%'
  }

  return baseStyle
})

const imageCropStyle = computed(() => {
  const style = {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    objectPosition: 'center center',
    display: 'block'
  }

  // If we have image dimensions, use them to calculate the aspect ratio
  const dims = props.banner?.image_crop_dimensions
  if (dims && dims.width > 0 && dims.height > 0) {
    style.aspectRatio = `${dims.width} / ${dims.height}`
  }

  return style
})

const backgroundStyle = computed(() => {
  if (!hasTextOverlay.value) {
    return {}
  }
  const style = {
    boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  }
  if (props.banner.text_background) {
    style.backgroundColor = props.banner.text_background
  }
  return style
})

const contentStyle = computed(() => {
  // On mobile/tablet (< 1280px), allow full width with auto height; on desktop (>= 1280px), constrain to 50%
  if (viewportWidth.value < 1280) {
    return {
      flex: '0 0 auto',
      maxWidth: '100%',
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
      boxSizing: 'border-box'
    }
  }
  return {
    flex: '1 1 50%',
    maxWidth: '50%',
    width: '50%',
    minWidth: 0,
    overflow: 'hidden',
    boxSizing: 'border-box'
  }
})

const textOrderClass = computed(() => {
  const mobileOrder = props.banner.mobile_order || (props.banner.text_position === 'right' ? 'text-first' : 'image-first')
  const isTextFirstMobile = mobileOrder === 'text-first'
  const isTextLeftDesktop = props.banner.text_position === 'right'

  if (isTextFirstMobile && isTextLeftDesktop) {
    return 'order-1 xl:order-1'
  } else if (isTextFirstMobile && !isTextLeftDesktop) {
    return 'order-1 xl:order-2'
  } else if (!isTextFirstMobile && isTextLeftDesktop) {
    return 'order-2 xl:order-1'
  } else {
    return 'order-2 xl:order-2'
  }
})

const imageOrderClass = computed(() => {
  const mobileOrder = props.banner.mobile_order || (props.banner.text_position === 'right' ? 'text-first' : 'image-first')
  const isTextFirstMobile = mobileOrder === 'text-first'
  const isTextLeftDesktop = props.banner.text_position === 'right'

  if (isTextFirstMobile && isTextLeftDesktop) {
    return 'order-2 xl:order-2'
  } else if (isTextFirstMobile && !isTextLeftDesktop) {
    return 'order-2 xl:order-1'
  } else if (!isTextFirstMobile && isTextLeftDesktop) {
    return 'order-1 xl:order-2'
  } else {
    return 'order-1 xl:order-1'
  }
})

</script>

<style scoped>
/* Smooth transition when resizing */
.flex {
  transition: flex-direction 0.3s ease;
}

/* Ensure image container respects borders and doesn't overflow */
.relative.overflow-hidden {
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.relative.overflow-hidden img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  margin: auto;
}

.relative.overflow-hidden a {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ensure proper stacking on mobile/tablet */
@media (max-width: 1279px) {
  .flex-col {
    flex-direction: column !important;
  }

  /* Full width items on mobile/tablet */
  .w-full {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* Remove any fixed heights on mobile/tablet */
  [class*="xl:flex-"] {
    height: auto !important;
  }
}

/* Desktop: Ensure flex items respect borders */
@media (min-width: 1280px) {
  .xl\\:flex-row>* {
    box-sizing: border-box;
  }

  /* Account for border width in flex calculations */
  .xl\\:border-l,
  .xl\\:border-r {
    border-width: 1px;
  }
}

/* Text Overflow and Word Breaking - Applied to all text containers */
.break-words,
:deep(*) {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

/* Ensure no element exceeds container width */
:deep(*) {
  max-width: 100%;
  box-sizing: border-box;
}

/* Prevent long URLs and continuous text from breaking layout */
:deep(a),
:deep(span),
:deep(div),
:deep(p) {
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

/* Comprehensive Quill Editor Content Rendering */
:deep(h1) {
  font-size: 2.5rem;
  font-weight: bold;
  color: inherit;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

:deep(h2) {
  font-size: 2rem;
  font-weight: bold;
  color: inherit;
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

:deep(h3) {
  font-size: 1.5rem;
  font-weight: bold;
  color: inherit;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

/* Font Sizes */
:deep(.ql-size-small) {
  font-size: 0.75rem;
}

:deep(.ql-size-large) {
  font-size: 1.5rem;
}

:deep(.ql-size-huge) {
  font-size: 2.5rem;
}

/* Blockquotes */
:deep(blockquote) {
  border-left: 4px solid #ccc;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  opacity: 0.8;
}

/* Code Blocks */
:deep(pre) {
  background-color: rgba(244, 244, 244, 0.9);
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1rem 0;
}

:deep(code) {
  background-color: rgba(244, 244, 244, 0.9);
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

:deep(pre code) {
  background-color: transparent;
  padding: 0;
}

/* Images */
:deep(img) {
  max-width: 100%;
  height: auto;
  margin: 1rem 0;
}

/* Videos and Iframes */
:deep(iframe),
:deep(video) {
  max-width: 100%;
  height: auto;
  margin: 1rem 0;
}

/* Text Alignment */
:deep(.ql-align-center) {
  text-align: center;
}

:deep(.ql-align-right) {
  text-align: right;
}

:deep(.ql-align-justify) {
  text-align: justify;
}

:deep([style*='text-align: center']) {
  text-align: center;
}

:deep([style*='text-align: right']) {
  text-align: right;
}

:deep([style*='text-align: justify']) {
  text-align: justify;
}

/* Font Families */
:deep(.ql-font-serif) {
  font-family: Georgia, 'Times New Roman', serif;
}

:deep(.ql-font-monospace) {
  font-family: 'Courier New', Courier, monospace;
}

/* Lists */
:deep(ul),
:deep(ol) {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

:deep(li) {
  margin-bottom: 0.5rem;
}

/* Links */
:deep(a) {
  color: inherit;
  text-decoration: underline;
  transition: opacity 0.2s;
}

:deep(a:hover) {
  opacity: 0.8;
}

/* Spacing */
:deep(p) {
  margin-bottom: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

/* Prevent text from overflowing horizontally */
:deep(pre),
:deep(code) {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

/* Ensure tables don't cause overflow */
:deep(table) {
  max-width: 100%;
  overflow-x: auto;
  display: block;
}
</style>
